import {inject, Injectable, OnDestroy} from '@angular/core';
import {EMPTY, merge, Observable, Subject} from 'rxjs';
import { take } from 'rxjs/operators';
import {ApBaseClass} from 'webcommon/shared/ap-base-class-ng1520';
import { injectNextHtmlId } from '../helpers';
import {GenericEventSink, RootEventSink, RootEventSinkToken} from './GenericEventSink';


// If we want this to extend BridgeService, the feature-browserhomepage branch in AppMobile has an example.
// The difference between this and BridgeService is that for the bridge, there's a separation between the events
// going C# => JS and JS => C#: they only can get handled on the "JS side" or "C# side".
// With this class below, all the events come thru here, regardless of whether they are coming from
// the "JS side" that previously would call the bridge directly, or the "web bridge" that is essentially replacing the C# bridge
// when we are in a standalone browser.
@Injectable({
  providedIn: 'root',
})
export class GenericEventHandlerService
extends ApBaseClass
implements OnDestroy, GenericEventSink {
  private readonly parentEventSink: RootEventSink | null = (() => {
    const rootEventSink = inject(RootEventSinkToken, {
      optional: true,
    });
    return rootEventSink && rootEventSink.canHandleEvents ? rootEventSink : null;
  })();

  onParent: <TData = never>(eventName: string) => Observable<TData> = this.parentEventSink ?
    eventName => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return this.parentEventSink!.on(eventName);
    } :
    () => EMPTY;

  onceParent: <TData = never>(eventName: string) => Observable<TData> = this.parentEventSink ?
    eventName => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return this.parentEventSink!.once(eventName);
    } :
    () => EMPTY;

  fireEventToParent: (eventName: string, data: any) => boolean = this.parentEventSink ?
    (eventName, data) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.parentEventSink!.fireEvent(eventName, data);
      return true;
    } :
    (eventName, data) => {
      console.error(`Received request to fire unknown named event: ${eventName}`, data);
      return false;
    };

  protected readonly streamByEventName = new Map<string, Subject<any | null>>();

  public readonly uniqueId: string;

  constructor() {
    super();
    const [_element, uniqueId] = injectNextHtmlId();
    this.uniqueId = uniqueId;
    console.log(`GenericEventHandlerService (${uniqueId}) initialized:`, this);
  }

  override ngOnDestroy(): void {
    this.streamByEventName.forEach((stream$, key, map) => {
      stream$.complete();
      map.delete(key);
    });
    super.ngOnDestroy();
  }

  protected _onJsEvent<TData = never>(
    eventName: string,
  ): Observable<TData> {
    return new Observable<TData>((observer) => {
      let eventStream$: Subject<TData> | undefined = this.streamByEventName.get(eventName);
      if (!eventStream$) {
        eventStream$ = new Subject<TData>();
        this.streamByEventName.set(eventName, eventStream$);
      }

      return eventStream$.subscribe(observer);
    });
  }

  on<TData = never>(
    eventName: string,
  ): Observable<TData> {
    const events$ = this._onJsEvent<TData>(eventName);
    return events$;
  }

  onAndParent<TData = never>(
    eventName: string,
  ): Observable<TData> {
    const events$ = this._onJsEvent<TData>(eventName);

    const parentEvents$ = this.onParent(eventName);

    return merge(events$, parentEvents$);
  }

  once<TData = never>(
    eventName: string,
  ): Observable<TData> {
    return this.on(eventName).pipe(
      take(1),
    );
  }

  onceAndParent<TData = never>(
    eventName: string,
  ): Observable<TData> {
    return this.onAndParent(eventName).pipe(
      take(1),
    );
  }

  // fireEvent:
  // manager => sink => n handlers ... (could conditionally fire to bridge as well)
  // OR
  // manager => sink => no handlers => bridge ...
  fireEvent(eventName: string, data: any): void {
    const eventStream$ = this.streamByEventName.get(eventName);
    if (eventStream$ && eventStream$.observers.length) {
      // If there is at least one handler, fire the event.
      // Normally, we shouldn't try to check observers, but in this case,
      // we are using it as a way to decide whether to "intercept" the event, OR delegate to the bridge.
      eventStream$.next(data);
      return;
    }

    // If no handler, try to delegate event to parent.
    // (normally, this would be for events fired from reusable components that are not specifically handled in homepage,
    // and need to make it to the bridge)
    this.fireEventToParent(eventName, data);
  }
}
